/**
 * Eligibility Step
 *
 * Second step of the intake wizard - collects loan and property information
 * to determine preliminary eligibility.
 */

import MultiIcon from '@mui/icons-material/Apartment';
import HomeIcon from '@mui/icons-material/Home';
import CommercialIcon from '@mui/icons-material/Store';
import {
  Box,
  FormControlLabel,
  GlassCard,
  GlassCheckbox,
  Grid,
  MagicInput,
  MagicSelect,
  MoneyField,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import type { IntakeFormData } from '../IntakeWizard';

interface EligibilityStepProps {
  formData: IntakeFormData;
  updateFormData: (updates: Partial<IntakeFormData>) => void;
}

// Loan purpose keys for i18n lookup
const LOAN_PURPOSE_KEYS = [
  {
    value: 'purchase',
    labelKey: 'onboarding.eligibility.loanPurpose.purchase',
    descKey: 'onboarding.eligibility.loanPurpose.purchase.desc',
  },
  {
    value: 'refinance',
    labelKey: 'onboarding.eligibility.loanPurpose.refinance',
    descKey: 'onboarding.eligibility.loanPurpose.refinance.desc',
  },
  {
    value: 'cash_out',
    labelKey: 'onboarding.eligibility.loanPurpose.cashOut',
    descKey: 'onboarding.eligibility.loanPurpose.cashOut.desc',
  },
  {
    value: 'bridge',
    labelKey: 'onboarding.eligibility.loanPurpose.bridge',
    descKey: 'onboarding.eligibility.loanPurpose.bridge.desc',
  },
] as const;

// Property type keys for i18n lookup
const PROPERTY_TYPE_KEYS = [
  { value: 'single_family', labelKey: 'onboarding.eligibility.propertyType.singleFamily', icon: <HomeIcon /> },
  { value: 'multi_family', labelKey: 'onboarding.eligibility.propertyType.multiFamily', icon: <MultiIcon /> },
  { value: 'commercial', labelKey: 'onboarding.eligibility.propertyType.commercial', icon: <CommercialIcon /> },
  { value: 'mixed_use', labelKey: 'onboarding.eligibility.propertyType.mixedUse', icon: <CommercialIcon /> },
] as const;

// Credit score range keys for i18n lookup
const CREDIT_RANGE_KEYS = [
  { value: 'excellent', labelKey: 'onboarding.eligibility.creditScore.excellent', color: 'success.main' },
  { value: 'good', labelKey: 'onboarding.eligibility.creditScore.good', color: 'info.main' },
  { value: 'fair', labelKey: 'onboarding.eligibility.creditScore.fair', color: 'warning.main' },
  { value: 'poor', labelKey: 'onboarding.eligibility.creditScore.poor', color: 'error.main' },
] as const;

export default function EligibilityStep({ formData, updateFormData }: EligibilityStepProps) {
  const { t } = useI18n();

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('onboarding.eligibility.title')}
      </Typography>
      <Typography color="textSecondary" sx={{ mb: 3 }}>
        {t('onboarding.eligibility.description')}
      </Typography>

      {/* Loan Purpose */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          {t('onboarding.eligibility.loanPurpose')}
        </Typography>
        <Grid container spacing={2}>
          {LOAN_PURPOSE_KEYS.map((purpose) => (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={purpose.value}>
              <GlassCard
                onClick={() => updateFormData({ loanPurpose: purpose.value as IntakeFormData['loanPurpose'] })}
                sx={{
                  cursor: 'pointer',
                  border: formData.loanPurpose === purpose.value ? '2px solid' : '1px solid transparent',
                  borderColor: formData.loanPurpose === purpose.value ? 'primary.main' : 'transparent',
                  textAlign: 'center',
                  py: 2,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {t(purpose.labelKey)}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {t(purpose.descKey)}
                </Typography>
              </GlassCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Property Type */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          {t('onboarding.eligibility.propertyType')}
        </Typography>
        <Grid container spacing={2}>
          {PROPERTY_TYPE_KEYS.map((type) => (
            <Grid size={{ xs: 6, sm: 3 }} key={type.value}>
              <GlassCard
                onClick={() => updateFormData({ propertyType: type.value as IntakeFormData['propertyType'] })}
                sx={{
                  cursor: 'pointer',
                  border: formData.propertyType === type.value ? '2px solid' : '1px solid transparent',
                  borderColor: formData.propertyType === type.value ? 'primary.main' : 'transparent',
                  textAlign: 'center',
                  py: 2,
                }}
              >
                <Box sx={{ mb: 1 }}>{type.icon}</Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(type.labelKey)}
                </Typography>
              </GlassCard>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Loan Amount and Credit */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MoneyField
            label={t('onboarding.eligibility.estimatedLoanAmount')}
            value={formData.estimatedLoanAmount}
            onChange={(next) => updateFormData({ estimatedLoanAmount: next })}
            helperText={t('onboarding.eligibility.estimatedLoanAmountHelper')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <MagicSelect
            fullWidth
            label={t('onboarding.eligibility.creditScoreRange')}
            value={formData.creditScoreRange}
            onChange={(e) => updateFormData({ creditScoreRange: e.target.value as IntakeFormData['creditScoreRange'] })}
            options={[
              { value: '', label: t('onboarding.eligibility.selectRange') },
              ...CREDIT_RANGE_KEYS.map((range) => ({
                value: range.value,
                label: t(range.labelKey),
              })),
            ]}
          />
        </Grid>
      </Grid>

      {/* Experience */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
          {t('onboarding.eligibility.experience')}
        </Typography>
        <FormControlLabel
          control={
            <GlassCheckbox
              checked={formData.hasExperience}
              onChange={(e) => updateFormData({ hasExperience: e.target.checked })}
            />
          }
          label={t('onboarding.eligibility.hasExperience')}
        />
        {formData.hasExperience && (
          <MagicInput
            fullWidth
            label={t('onboarding.eligibility.numberOfProperties')}
            type="number"
            value={formData.numberOfProperties}
            onChange={(e) => updateFormData({ numberOfProperties: e.target.value })}
            sx={{ mt: 2 }}
            helperText={t('onboarding.eligibility.propertiesHelper')}
          />
        )}
      </Box>
    </Box>
  );
}
